import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  createMaintenanceBanner,
  deleteMaintenanceBanner,
  getMaintenanceBannerForAdmin,
  updateMaintenanceBanner,
  type MaintenanceBanner,
} from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toDatetimeLocalET } from "@/lib/time";

const emptyBannerForm = {
  message: "",
  banner_starts_at: "",
  banner_ends_at: "",
  maintenance_starts_at: "",
  maintenance_ends_at: "",
  is_active: true,
};

export function useMaintenanceBannerViewModel() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [bannerForm, setBannerForm] = React.useState(emptyBannerForm);
  const [bannerEditingId, setBannerEditingId] = React.useState<number | null>(null);
  const { data: manageableBannerData } = useQuery({
    queryKey: ["maintenance-banner-manage"],
    queryFn: getMaintenanceBannerForAdmin,
    enabled: user?.role === "admin",
    refetchInterval: 5 * 60 * 1000,
  });
  const manageableBanner: MaintenanceBanner | null = manageableBannerData ?? null;
  const createBannerMutation = useMutation({
    mutationFn: () =>
      createMaintenanceBanner({
        message: bannerForm.message,
        banner_starts_at: bannerForm.banner_starts_at || null,
        banner_ends_at: bannerForm.banner_ends_at || null,
        maintenance_starts_at: bannerForm.maintenance_starts_at || null,
        maintenance_ends_at: bannerForm.maintenance_ends_at || null,
        is_active: bannerForm.is_active,
      }),
    onSuccess: async () => {
      setBannerForm(emptyBannerForm);
      setBannerEditingId(null);
      await queryClient.invalidateQueries({ queryKey: ["maintenance-banner"] });
      await queryClient.invalidateQueries({ queryKey: ["maintenance-banner-manage"] });
    },
  });
  const updateBannerMutation = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: Parameters<typeof updateMaintenanceBanner>[1] }) =>
      updateMaintenanceBanner(id, payload),
    onSuccess: async () => {
      setBannerEditingId(null);
      setBannerForm(emptyBannerForm);
      await queryClient.invalidateQueries({ queryKey: ["maintenance-banner"] });
      await queryClient.invalidateQueries({ queryKey: ["maintenance-banner-manage"] });
    },
  });
  const deleteBannerMutation = useMutation({
    mutationFn: (id: number) => deleteMaintenanceBanner(id),
    onSuccess: async () => {
      setBannerForm(emptyBannerForm);
      setBannerEditingId(null);
      await queryClient.invalidateQueries({ queryKey: ["maintenance-banner"] });
      await queryClient.invalidateQueries({ queryKey: ["maintenance-banner-manage"] });
    },
  });
  return {
    bannerForm, setBannerForm, bannerEditingId, setBannerEditingId,
    emptyBannerForm, manageableBanner,
    createBannerMutation, updateBannerMutation, deleteBannerMutation,
    toDatetimeLocalET,
  };
}
